/**
 * HmContractorDetail — karta kontrahenta z kartoteki Symfonii (TYLKO PODGLĄD).
 *
 * Kartoteka jest oknem na cudzy system: nie edytujemy i nie kopiujemy niczego do naszej bazy.
 * Stąd brak przycisków zapisu — tak samo jak na formatkach dokumentów sprzedaży.
 *
 * NALEŻNOŚCI I ZOBOWIĄZANIA są u góry, nie w zakładce: to jedyna liczba, dla której handlowiec
 * otwiera kartę kontrahenta w trakcie rozmowy, więc nie może wymagać dodatkowego kliknięcia.
 *
 * PESEL nie jest pobierany ani pokazywany (decyzja właściciela 2026-09-14).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  HmContractorAddress,
  HmContractorDetail as HmContractorDetailDto,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  DetailError,
  DetailTabBody,
  DetailTabs,
  Field,
  Section,
} from '@/components/handel-magazyn/detail-parts';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TABS = ['addresses', 'contact', 'trade'] as const;
type Tab = (typeof TABS)[number];

/** Adres kontrahenta → linie do wyświetlenia. Pusty adres wraca jako pusta lista, nie pusty blok. */
function addressLines(address: HmContractorAddress | null): string[] {
  if (!address) return [];
  if (address.fullAddress) return [address.fullAddress];
  const street = [address.street, address.houseNo, address.apartmentNo && `/${address.apartmentNo}`]
    .filter(Boolean)
    .join(' ');
  const city = [address.postCode, address.city].filter(Boolean).join(' ');
  return [street, city, address.province, address.country].filter(
    (line): line is string => !!line && line.trim() !== '',
  );
}

function AddressCard({ title, address }: { title: string; address: HmContractorAddress | null }) {
  const lines = addressLines(address);
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-text-primary">{title}</h3>
      {lines.length ? (
        lines.map((line) => (
          <p key={line} className="text-sm text-text-secondary">
            {line}
          </p>
        ))
      ) : (
        <p className="text-sm text-text-tertiary">—</p>
      )}
    </div>
  );
}

export function HmContractorDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = useActiveCompanyScope().companyId;
  const [tab, setTab] = useState<Tab>('addresses');

  const query = useQuery({
    queryKey: ['handel-magazyn', 'contractor', id, companyId],
    enabled: !!id && !!companyId,
    retry: false,
    queryFn: async (): Promise<HmContractorDetailDto> => {
      const res = await api.get<HmContractorDetailDto>(
        `/handel-magazyn/contractors/${id}?companyId=${companyId}`,
      );
      return res.data;
    },
  });

  if (query.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }
  if (query.error) return <DetailError error={query.error} />;

  const c = query.data!;
  const yesNo = (value: boolean) => (value ? t('common.yes') : t('common.no'));

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">{c.name}</h1>
          <p className="text-sm text-text-secondary">
            {c.code} · {t('tenant.handelMagazyn.contractors.readOnly')}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Pencil className="h-4 w-4" />}
          onClick={() => navigate(`/handel-magazyn/kontrahenci/${c.id}/edycja`)}
          data-testid="hm-contractor-edit"
        >
          {t('common.edit')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.contractors.identity')}>
            <Field label={t('tenant.handelMagazyn.contractors.columns.taxId')} value={c.taxId} />
            <Field label={t('tenant.handelMagazyn.contractors.regon')} value={c.regon} />
            <Field
              label={t('tenant.handelMagazyn.columns.status')}
              value={
                c.active
                  ? t('tenant.handelMagazyn.contractors.active')
                  : t('tenant.handelMagazyn.contractors.inactive')
              }
            />
            <Field label={t('tenant.handelMagazyn.contractors.kind')} value={c.kindCode} />
          </Section>
        </div>

        <div className="space-y-4">
          {/* Rozrachunki — powód, dla którego kartę kontrahenta otwiera się najczęściej. */}
          <Section title={t('tenant.handelMagazyn.contractors.balance')}>
            <Field label={t('tenant.handelMagazyn.contractors.due')} value={c.balance.due} />
            <Field
              label={t('tenant.handelMagazyn.contractors.obligation')}
              value={c.balance.obligation}
            />
          </Section>
        </div>
      </div>

      <DetailTabs tabs={TABS} active={tab} onChange={setTab} />

      <DetailTabBody>
        {tab === 'addresses' && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <AddressCard
              title={t('tenant.handelMagazyn.contractors.defaultAddress')}
              address={c.defaultAddress}
            />
            <AddressCard
              title={t('tenant.handelMagazyn.contractors.correspondenceAddress')}
              address={c.correspondenceAddress}
            />
            {c.deliveryAddresses.length > 0 && (
              <div className="md:col-span-2">
                <h3 className="mb-1 text-sm font-semibold text-text-primary">
                  {t('tenant.handelMagazyn.contractors.deliveryAddresses')}
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {c.deliveryAddresses.map((addr, index) => (
                    <AddressCard
                      key={addr.fullAddress ?? `${addr.city}-${addr.street}-${index}`}
                      title={`${index + 1}.`}
                      address={addr}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'contact' && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Section title={t('tenant.handelMagazyn.contractors.contact')}>
              <Field label={t('tenant.handelMagazyn.detail.name')} value={c.contact.name} />
              <Field label={t('tenant.handelMagazyn.contractors.phone')} value={c.contact.phone1} />
              <Field
                label={t('tenant.handelMagazyn.contractors.phone2')}
                value={c.contact.phone2}
              />
              <Field label={t('tenant.handelMagazyn.contractors.email')} value={c.contact.email} />
              <Field label={t('tenant.handelMagazyn.contractors.www')} value={c.contact.www} />
            </Section>
            <Section title={t('tenant.handelMagazyn.contractors.bank')}>
              <Field
                label={t('tenant.handelMagazyn.contractors.accountNumber')}
                value={c.bank.accountNumber}
              />
              <Field
                label={t('tenant.handelMagazyn.contractors.bankName')}
                value={c.bank.bankName}
              />
              <Field label={t('tenant.handelMagazyn.contractors.swift')} value={c.bank.swift} />
            </Section>
          </div>
        )}

        {tab === 'trade' && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Section title={t('tenant.handelMagazyn.contractors.terms')}>
              <Field
                label={t('tenant.handelMagazyn.contractors.discount')}
                value={c.defaultDiscountPercent}
              />
              <Field
                label={t('tenant.handelMagazyn.contractors.priceNegotiation')}
                value={yesNo(c.priceNegotiation)}
              />
              <Field
                label={t('tenant.handelMagazyn.contractors.creditLimit')}
                value={c.creditLimit ? c.maxCreditValue : yesNo(false)}
              />
            </Section>
            <Section title={t('tenant.handelMagazyn.contractors.tax')}>
              <Field
                label={t('tenant.handelMagazyn.contractors.vatTaxPayer')}
                value={yesNo(c.vatTaxPayer)}
              />
              <Field label={t('tenant.handelMagazyn.contractors.vies')} value={yesNo(c.vies)} />
              <Field label={t('tenant.handelMagazyn.contractors.note')} value={c.note} />
            </Section>
          </div>
        )}
      </DetailTabBody>
    </div>
  );
}

export default HmContractorDetail;
